/**
 * Listing-draft builders — the boundary between the drafts hook
 * (`hooks/use-listing-drafts.ts`) and the HTTP routes
 * `app/api/listings/drafts/{save,delete}/route.ts`.
 *
 * Same shape as `lib/listings/listing-create.ts` (Sibling-E template):
 * safeParse against the @kraahl/contract schema → `seal()` → `postJson` —
 * a hand-rolled literal in a component cannot reach the routes.
 *
 * @see packages/contract/src/sell-draft.ts — wire schemas
 */

import {
  DeleteListingDraftRequestSchema,
  SaveListingDraftRequestSchema,
  SaveListingDraftResponseSchema,
  seal,
  type DeleteListingDraftRequest,
  type SaveListingDraftRequest,
  type SaveListingDraftResponse,
} from "@kraahl/contract"

import { postJson } from "@/lib/api/post-json"

/**
 * Thrown on contract-parse failure (programmer bug — the hook assembled an
 * intent the contract refuses) or on any non-2xx. `code` surfaces the
 * server's machine-readable rejection (`DRAFT_LIMIT_REACHED` on the 409 cap).
 */
export class ListingDraftError extends Error {
  readonly status: number | null
  readonly code: string | null
  constructor(message: string, status: number | null, code: string | null) {
    super(message)
    this.name = "ListingDraftError"
    this.status = status
    this.code = code
  }
}

async function throwFromResponse(scope: string, res: Response): Promise<never> {
  const payload = (await res.json().catch(() => null)) as {
    error?: unknown
    code?: unknown
  } | null
  const serverError = typeof payload?.error === "string" ? payload.error : null
  const code = typeof payload?.code === "string" ? payload.code : null
  throw new ListingDraftError(serverError ?? `${scope} (${res.status})`, res.status, code)
}

export async function saveListingDraft(
  intent: SaveListingDraftRequest,
): Promise<SaveListingDraftResponse> {
  const parsed = SaveListingDraftRequestSchema.safeParse(intent)
  if (!parsed.success) {
    throw new ListingDraftError(
      `[listings/drafts/save] contract parse failed: ${parsed.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; ")}`,
      null,
      null,
    )
  }

  const res = await postJson(
    "/api/listings/drafts/save",
    seal<SaveListingDraftRequest>(parsed.data),
  )
  if (!res.ok) await throwFromResponse("Save draft", res)

  const json = SaveListingDraftResponseSchema.safeParse(await res.json())
  if (!json.success) {
    throw new ListingDraftError("Save draft: malformed response", res.status, null)
  }
  return json.data
}

export async function deleteListingDraft(
  intent: DeleteListingDraftRequest,
): Promise<void> {
  const parsed = DeleteListingDraftRequestSchema.safeParse(intent)
  if (!parsed.success) {
    throw new ListingDraftError(
      `[listings/drafts/delete] contract parse failed: ${parsed.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; ")}`,
      null,
      null,
    )
  }

  const res = await postJson(
    "/api/listings/drafts/delete",
    seal<DeleteListingDraftRequest>(parsed.data),
  )
  if (!res.ok) await throwFromResponse("Delete draft", res)
}
